import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import axiosClient from '../../actions/axios-client';

/** Fills space to the right of the festival upload so QR layouts can use full row width */
const Outer = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const figureImg = `
  display: block;
  max-width: 150px;
  max-height: 150px;
  width: auto;
  height: auto;
  object-fit: contain;
`;

const figureCaption = `
  font-size: 0.75rem;
  color: #9b9b9b;
  margin-top: 0.35rem;
  line-height: normal;
`;

const FigureBase = styled.figure`
  margin: 0;
  text-align: center;
  max-width: 160px;

  img {
    ${figureImg}
  }

  figcaption {
    ${figureCaption}
  }
`;

const SingleWrap = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  flex: 1;
  min-width: 0;
`;

/** Equal flex sides place the public QR in the horizontal center; private sits flush right */
const DualRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
`;

const FlexSide = styled.div`
  flex: 1 1 0;
  min-width: 0;
`;

const FlexSideEnd = styled.div`
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
`;

/** Reads `qr-code-text` from Axios plain headers or AxiosHeaders (axios 1.x). */
function getQrCodeTextHeader(headers) {
  if (!headers) return null;
  const raw =
    typeof headers.get === 'function'
      ? headers.get('qr-code-text')
      : headers['qr-code-text'];
  const s = typeof raw === 'string' ? raw.trim() : '';
  return s ? s : null;
}

const CaptionLink = styled.a`
  color: #9b9b9b;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: #c4c4c4;
  }
`;

/** Stronger than FigureBase figcaption so missing-header styling wins */
const CaptionMissing = styled.figcaption`
  && {
    font-size: 0.75rem;
    color: #c62828;
    margin-top: 0.35rem;
    line-height: normal;
  }
`;

function QrCaption({ href, children }) {
  if (href) {
    return (
      <figcaption>
        <CaptionLink href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </CaptionLink>
      </figcaption>
    );
  }
  return <CaptionMissing>{children}</CaptionMissing>;
}

export const FestivalPromotionImages = ({ festivalId, status }) => {
  const [publicUrl, setPublicUrl] = useState(null);
  const [privateUrl, setPrivateUrl] = useState(null);
  const [publicLinkHref, setPublicLinkHref] = useState(null);
  const [privateLinkHref, setPrivateLinkHref] = useState(null);
  const urlsRef = useRef([]);

  useEffect(() => {
    urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    urlsRef.current = [];
    setPublicUrl(null);
    setPrivateUrl(null);
    setPublicLinkHref(null);
    setPrivateLinkHref(null);

    if (!festivalId || status === 'DELETED') {
      return undefined;
    }

    let cancelled = false;

    const load = async () => {
      const wantPublic = status === 'PUBLISHED' || status === 'PREVIEW';
      const wantPrivate = status === 'UNPUBLISHED' || status === 'PREVIEW';

      const fetches = [];
      if (wantPublic) {
        fetches.push(
          axiosClient
            .get(`/admin/festival/${festivalId}/qrcode`, {
              responseType: 'blob',
              headers: { Accept: 'image/png' },
            })
            .then((r) => ({
              key: 'public',
              blob: r.data,
              linkHref: getQrCodeTextHeader(r.headers),
            }))
        );
      }
      if (wantPrivate) {
        fetches.push(
          axiosClient
            .get(`/admin/festival/${festivalId}/previewCode`, {
              responseType: 'blob',
              headers: { Accept: 'image/png' },
            })
            .then((r) => ({
              key: 'private',
              blob: r.data,
              linkHref: getQrCodeTextHeader(r.headers),
            }))
        );
      }

      try {
        const results = await Promise.all(fetches);
        if (cancelled) return;
        for (const { key, blob, linkHref } of results) {
          const u = URL.createObjectURL(blob);
          urlsRef.current.push(u);
          if (key === 'public') {
            setPublicUrl(u);
            setPublicLinkHref(linkHref);
          }
          if (key === 'private') {
            setPrivateUrl(u);
            setPrivateLinkHref(linkHref);
          }
        }
      } catch {
        /* ignore — optional: surface toast */
      }
    };

    load();

    return () => {
      cancelled = true;
      urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
      urlsRef.current = [];
    };
  }, [festivalId, status]);

  if (!festivalId || status === 'DELETED') {
    return null;
  }

  const showPublic = status === 'PUBLISHED' || status === 'PREVIEW';
  const showPrivate = status === 'UNPUBLISHED' || status === 'PREVIEW';

  if (!showPublic && !showPrivate) {
    return null;
  }

  const hasPublic = showPublic && !!publicUrl;
  const hasPrivate = showPrivate && !!privateUrl;
  const count = (hasPublic ? 1 : 0) + (hasPrivate ? 1 : 0);

  if (count === 0) {
    return <Outer />;
  }

  if (count === 1) {
    return (
      <Outer>
        <SingleWrap>
          {hasPublic && (
            <FigureBase>
              <img src={publicUrl} alt="QR code for public festival link" />
              <QrCaption href={publicLinkHref}>Public Link</QrCaption>
            </FigureBase>
          )}
          {hasPrivate && (
            <FigureBase>
              <img src={privateUrl} alt="QR code for private preview" />
              <QrCaption href={privateLinkHref}>Private Preview</QrCaption>
            </FigureBase>
          )}
        </SingleWrap>
      </Outer>
    );
  }

  return (
    <Outer>
      <DualRow>
        <FlexSide aria-hidden="true" />
        {hasPublic && (
          <FigureBase>
            <img src={publicUrl} alt="QR code for public festival link" />
            <QrCaption href={publicLinkHref}>Public Link</QrCaption>
          </FigureBase>
        )}
        <FlexSideEnd>
          {hasPrivate && (
            <FigureBase>
              <img src={privateUrl} alt="QR code for private preview" />
              <QrCaption href={privateLinkHref}>Private Preview</QrCaption>
            </FigureBase>
          )}
        </FlexSideEnd>
      </DualRow>
    </Outer>
  );
};
